import { useState, useEffect } from 'react';
import { 
  User, 
  signInWithPopup, 
  signOut as firebaseSignOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { clearAllCaches } from '../utils/subscriptionCache';
import { clearAllQuotaCaches } from '../utils/quotaCache';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null
  });

  // Listen for auth state changes
  useEffect(() => {
    if (!auth) {
      // Auth is not available (e.g., during prerender) - mark as not loading with no user
      setAuthState({
        user: null,
        loading: false,
        error: 'Authentication is not available'
      });
      return;
    }

    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        // Clear caches when user changes (login/logout)
        if (!user) {
          clearAllCaches();
          clearAllQuotaCaches();
        }
        
        setAuthState({
          user,
          loading: false,
          error: null
        });
      },
      (error) => {
        console.error('Auth state change error:', error);
        setAuthState({
          user: null,
          loading: false,
          error: error.message
        });
      }
    );

    return () => unsubscribe();
  }, []);

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      if (!auth || !googleProvider) {
        throw new Error('Authentication is not available');
      }
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      const errorMessage = error.code === 'auth/popup-closed-by-user' 
        ? 'Sign-in was cancelled'
        : error.message || 'Sign-in failed';
      
      setAuthState(prev => ({ 
        ...prev, 
        loading: false, 
        error: errorMessage 
      }));
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      if (!auth) {
        throw new Error('Authentication is not available');
      }
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      await firebaseSignOut(auth);
      // Clear all caches on logout
      clearAllCaches();
      clearAllQuotaCaches();
    } catch (error: any) {
      console.error('Sign-out error:', error);
      setAuthState(prev => ({ 
        ...prev, 
        loading: false, 
        error: error.message || 'Sign-out failed' 
      }));
      throw error;
    }
  };

  // Clear error
  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null }));
  };

  return {
    user: authState.user,
    loading: authState.loading,
    error: authState.error,
    signInWithGoogle,
    signOut,
    clearError
  };
};
